/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Table component for import personal data wizard
 *
 * @author Nikolay Sviridov
 * @version 1.33.0
 * @since 1.33.0
 */

import { Fragment, useEffect } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { Table } from '../../../../components/common';
import { loadTableListAction } from '../../../../components/Table/actions/tableActions';
import { ITableField, ITableReducer } from '../../../../components/Table/reducers/tableReducer';
import {
    makeSelectorTableCount,
    makeSelectorTableData,
    makeSelectorTableFields,
    makeSelectorTableIsLoading, makeSelectorTableRemovedRowIndexes,
} from '../../../../components/Table/selectors/tableSelectors';
import TableColumnUtil, { ETableColumnUtilHideFilters } from '../../../../components/Table/TableColumnUtil';
import { ILoadTableListPayload, TableNameType } from '../../../../components/Table/types/tableTypes';
import { makeSelectTranslationCommon } from '../../../../modules/internationalization/intSelectors';
import { ICommonPageTranslation, ICommonTranslation } from '../../../../modules/internationalization/types';
import { ICommonPageTranslationTitle } from '../../../../modules/internationalization/types/pages/common';
import {
    IImportSubscribersLimitsTableTranslation,
    IWizardImportSubscribersLimitsTranslation,
} from '../../../../modules/internationalization/types/wizards/importSubscribersLimits';
import { guardComponentType } from '../../../../modules/role/reducer/guardReducer';
import {
    makeSelectGuardComponents,
    makeSelectIsColumnResizeEnabled,
} from '../../../../modules/role/selectors/guardSelector';
import { makeSelectIsDensity } from '../../../../modules/storage/storageSelectors';
import { IRootReducer } from '../../../../reducers/rootReducer';
import { wizardImportSubscribersLimitsInitValidatedList } from '../../wizardImportSubscribersLimitsActions';
import { EImportSubscribersLimitsField } from '../../wizardImportSubscribersLimitsConstant';
import {
    getColumnCellType,
    getColumnFilterType,
    getColumnFixedType,
    importSubscribersLimitsColumnNamesFinalStep,
    importSubscribersLimitsColumnsWidth,
} from './WizardImportSubscribersLimitsTableCells';

interface IWizardImportSubscribersLimitsTableProps {
    translation: IWizardImportSubscribersLimitsTranslation;
}

export type TWizardImportSubscribersLimitsTable = 
    IWizardImportSubscribersLimitsTableProps & IConnectedProps & IDispatchProps;

export const WizardImportSubscribersLimitsTable = (props: TWizardImportSubscribersLimitsTable) => {
    const tableColumnUtil = new TableColumnUtil<EImportSubscribersLimitsField,
        ICommonPageTranslation<IImportSubscribersLimitsTableTranslation> & ICommonTranslation>({
            cellConfig: importSubscribersLimitsColumnsWidth,
            componentsFeature: props.componentsFeature,
            fields: props.fields as Array<ITableField<EImportSubscribersLimitsField>>,
            isTableResizeEnabled: props.isTableResizeEnabled,
            getColumnCellType,
            getColumnFilterType,
            getColumnFixedType,
            columnNames: importSubscribersLimitsColumnNamesFinalStep,
            hideFiltersType: ETableColumnUtilHideFilters.EMPTY,
            isDisableSort: true,
            translation: {
                ...props.translationCommon,
                table: { ...props.translation.table },
                title: {} as ICommonPageTranslationTitle,
            },
        },
    );

    useEffect(() => {
        props.initValidatedList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Fragment>
            <Table
                columns={tableColumnUtil.customizeTableColumns()}
                counter={props.tableCount}
                data={props.data}
                isDensity={props.isDensity}
                isLoading={props.isLoading}
                loadTableList={props.loadTableList}
                removedRowIndexes={props.removedRowIndexes}
                scrollbarBottom='64px'
                setTableSort={() => {
                }}
                sort={{}}
                tableDataName="importSubscribersLimits"
            />
        </Fragment>
    );
};

interface IConnectedProps {
    componentsFeature: guardComponentType;
    data: ITableReducer[TableNameType]['data'];
    fields: ITableReducer[TableNameType]['fields'];
    isDensity: boolean;
    isLoading: boolean;
    isTableResizeEnabled: boolean;
    removedRowIndexes: number[];
    tableCount: number;
    translationCommon: ICommonTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    componentsFeature: makeSelectGuardComponents(),
    data: makeSelectorTableData(TableNameType.importSubscribersLimits),
    fields: makeSelectorTableFields(TableNameType.importSubscribersLimits),
    isDensity: makeSelectIsDensity(),
    isLoading: makeSelectorTableIsLoading(TableNameType.importSubscribersLimits),
    isTableResizeEnabled: makeSelectIsColumnResizeEnabled(),
    removedRowIndexes: makeSelectorTableRemovedRowIndexes(TableNameType.importSubscribersLimits),
    tableCount: makeSelectorTableCount(TableNameType.importSubscribersLimits),
    translationCommon: makeSelectTranslationCommon(),
});

interface IDispatchProps {
    initValidatedList: () => void;
    loadTableList: (payload?: ILoadTableListPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    initValidatedList: () => dispatch(wizardImportSubscribersLimitsInitValidatedList()),
    loadTableList: (payload) => dispatch(loadTableListAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardImportSubscribersLimitsTable);
